import { Check } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { FounderLevel } from "@/types/levels";

interface LevelsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentLevel?: FounderLevel | null;
}

const levels = [
  { key: "starter", name: "Stack Starter" },
  { key: "certified", name: "Stack Certified" },
  { key: "pro", name: "Stack Pro" },
  { key: "x", name: "Stack X" },
];

const features: Array<{
  label: string;
  availability: Record<string, boolean | "-" | string>;
}> = [
  {
    label: "Stack business tools",
    availability: { starter: true, certified: true, pro: true, x: true },
  },
  {
    label: "Stack playbook",
    availability: { starter: true, certified: true, pro: true, x: true },
  },
  {
    label: "Stack community",
    availability: { starter: true, certified: true, pro: true, x: true },
  },
  {
    label: "Stack business advisor",
    availability: { starter: true, certified: true, pro: true, x: true },
  },
  {
    label: "Live sessions access",
    availability: { starter: true, certified: true, pro: true, x: true },
  },
  {
    label: "Access to leads through Marketplace",
    availability: { starter: "-", certified: true, pro: true, x: true },
  },
];

const LevelsDialog = ({
  open,
  onOpenChange,
  currentLevel = null,
}: LevelsDialogProps) => {
  const isMobile = useIsMobile();
  const currentKey = (() => {
    if (currentLevel === FounderLevel.STACK_CERTIFIED) return "certified";
    if (currentLevel === FounderLevel.STACK_PRO) return "pro";
    if (currentLevel === FounderLevel.STACK_X) return "x";
    return "starter";
  })();

  // Mobile layout: vertical cards for each level
  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[95vw] max-h-[85vh] p-0 bg-white rounded-2xl border-0 overflow-hidden">
          <div className="p-4 overflow-y-auto max-h-[80vh]">
            <h2 className="text-lg font-semibold text-[#1e1e24] mb-4">Stack Levels</h2>
            <div className="space-y-4">
              {levels.map((lvl) => (
                <div
                  key={lvl.key}
                  className={cn(
                    "rounded-xl p-4 border",
                    lvl.key === currentKey
                      ? "border-[#dcddfa] bg-[#f8f8ff]"
                      : "border-[#e8e8e8] bg-[#f9f9f9]"
                  )}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[#494949] text-base font-semibold">
                      {lvl.name}
                    </span>
                    {lvl.key === currentKey && (
                      <span className="inline-flex items-center rounded-full bg-[#dcddfa] text-[#4f55e8] text-[10px] font-semibold px-2 py-[2px]">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {features.map((row, idx) => {
                      const value = row.availability[lvl.key];
                      const isAvailable = value === true;
                      const isNotAvailable = value === "-" || value === false;
                      return (
                        <div
                          key={idx}
                          className={cn(
                            "flex items-center gap-2 text-sm",
                            isNotAvailable && "opacity-50"
                          )}
                        >
                          {isAvailable ? (
                            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#83b300] flex-shrink-0">
                              <Check className="h-2.5 w-2.5 text-white" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center h-4 w-4 text-[#767677] flex-shrink-0">
                              -
                            </span>
                          )}
                          <span className={cn(
                            "text-[#494949]",
                            isNotAvailable && "text-[#767677]"
                          )}>
                            {row.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop layout: table view
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1120px] p-0 bg-white rounded-2xl border-0">
        <div className="p-4 md:p-6">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Columns layout (labels + four level columns) */}
              <div className="grid grid-cols-[minmax(280px,auto)_repeat(4,1fr)] gap-x-4 items-start">
                {/* Labels column */}
                <div className="flex flex-col p-2">
                  <div className="min-h-[56px] md:min-h-[64px]" />
                  {features.map((row, rowIndex) => (
                    <div key={rowIndex} className="px-4 md:px-6 py-2 md:py-2.5">
                      <div className="text-[#494949] text-sm font-semibold tracking-[-0.56px]">
                        {row.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Level columns */}
                {levels.map((lvl) => (
                  <div
                    key={lvl.key}
                    className={cn(
                      "relative flex flex-col items-center px-2 py-2",
                      lvl.key === currentKey && "bg-white rounded-2xl",
                    )}
                  >
                    {lvl.key === currentKey && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-2xl border border-[#dcddfa]"
                      />
                    )}
                    <div className="text-center min-h-[56px] md:min-h-[64px] flex flex-col items-center justify-center w-full">
                      <div className="text-[#494949] text-sm font-semibold tracking-[-0.56px]">
                        {lvl.name}
                      </div>
                      {lvl.key === currentKey && (
                        <div className="inline-flex items-center rounded-full bg-[#dcddfa] text-[#4f55e8] text-[10px] font-semibold tracking-[-0.4px] px-2 py-[2px] mt-1">
                          Current
                        </div>
                      )}
                    </div>

                    {features.map((row, idx) => {
                      const value = row.availability[lvl.key];
                      return (
                        <div
                          key={idx}
                          className="flex justify-center items-center px-4 md:px-6 py-2 md:py-2.5 w-full"
                        >
                          {typeof value === "string" ? (
                            <span
                              className={cn(
                                "text-sm font-semibold tracking-[-0.56px]",
                                value === "-"
                                  ? "text-[#767677]"
                                  : "text-[#767677]",
                              )}
                            >
                              {value}
                            </span>
                          ) : value === true ? (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#83b300]">
                              <Check className="h-3.5 w-3.5 text-white" />
                            </span>
                          ) : (
                            <span className="text-[#767677] text-sm">-</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LevelsDialog;
